/**
 * Dr. Herança Tenant Configuration
 * 
 * This file exports the configuration for the Dr. Herança tenant.
 */

import { TenantConfig, MenuItems, UserConfig } from 'src/core/types/appConfig';
import { CoreLayout, CoreComponent } from 'src/core/routes';
import PersonIcon from '@mui/icons-material/Person';
import RecentActors from '@mui/icons-material/RecentActors';
import FeedIcon from '@mui/icons-material/Feed';
import TreeIcon from '@mui/icons-material/AccountTreeTwoTone';
import GroupIcon from '@mui/icons-material/Group';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import DescriptionIcon from '@mui/icons-material/Description';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import GavelIcon from '@mui/icons-material/Gavel';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DrHerancaComponent } from './routesTypes';

/** When `REACT_APP_HIDE_INVENTORY_DOCUMENTS_SECTION` is `true`, hides "Andamento do Inventário e Gestão de Documentos" on the inventory home. */
export const hideInventoryDocumentsSection =
  String(process.env.REACT_APP_HIDE_INVENTORY_DOCUMENTS_SECTION || '').toLowerCase() === 'true';

export const drherancaConfig: TenantConfig = {
    id: 'drheranca',
    name: 'Dr. Herança',
    description: 'Sistema de Cálculo de Inventário e Partilha de Bens',
    
    api: {
        url: process.env.REACT_APP_DR_HERANCA_API || '',
    },
    
    assets: {
        logo: '/static/tenants/drheranca/images/logo/logo.png',
        favicon: '/static/tenants/drheranca/images/favicon.ico',
    },
    
    theme: {
        primaryColor: '#5569ff',
        secondaryColor: '#6E759F',
        accentColor: '#9B2335',
        accentColorLight: '#f7f7f7',
        mode: 'light',
    },

    seo: {
        tagline: 'Calculadora de inventário, simulador de custas e partilha de bens com ITCMD e relatórios',
        keywords:
            'calculadora de inventário, simulador de custos de inventário, cálculo de partilha de bens online, simulador ITCMD, inventário, partilha, herança, ITCMD, meação, Dr. Herança',
        author: 'Dr. Herança',
        ogImage: '/static/tenants/drheranca/images/og-image.png',
        twitterImage: '/static/tenants/drheranca/images/twitter-image.png',
    },

    user: {
        useUserInfo: () => {
            // Lazy import to avoid circular dependency
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const { useGetUserInfoQuery } = require('./api');
            const result = useGetUserInfoQuery(null, {
                refetchOnMountOrArgChange: true
            });
            // Adapt RTK Query result to expected format
            return {
                data: result.data ? { user: result.data.user } : undefined,
                isLoading: result.isLoading
            };
        },
        menuItems: [
            {
                name: 'Minha Conta',
                link: '/user/profile',
                icon: PersonIcon,
            },
            {
                name: 'Inventários',
                link: '/inventories/list',
                icon: RecentActors,
            },
        ],
    },
    
    routeConfig: [
        {
            type: 'group',
            path: '/',
            layout: CoreLayout.BaseLayout,
            children: [
                {
                    type: 'page',
                    path: '/',
                    component: DrHerancaComponent.Home,
                },
                {
                    type: 'redirect',
                    path: 'home',
                    redirectTo: '/',
                },
                {
                    type: 'page',
                    path: 'blog',
                    component: CoreComponent.BlogList,
                },
                {
                    type: 'page',
                    path: 'blog/:id',
                    component: CoreComponent.BlogArticle,
                },
                {
                    type: 'page',
                    path: 'calculadora-inventario',
                    component: DrHerancaComponent.LandingCalculadoraInventario,
                },
                {
                    type: 'redirect',
                    path: 'calculadora-inventario/:uf',
                    redirectTo: '/calculadora-inventario',
                },
                {
                    type: 'group',
                    path: 'status',
                    children: [
                        {
                            type: 'redirect',
                            path: '/status/',
                            redirectTo: '404',
                        },
                        {
                            type: 'page',
                            path: '404',
                            component: CoreComponent.Status404,
                        },
                        {
                            type: 'page',
                            path: '500',
                            component: CoreComponent.Status500,
                        },
                        {
                            type: 'page',
                            path: 'maintenance',
                            component: CoreComponent.Maintenance,
                        },
                        {
                            type: 'page',
                            path: 'coming-soon',
                            component: CoreComponent.ComingSoon,
                        },
                    ],
                },
                {
                    type: 'page',
                    path: '*',
                    component: CoreComponent.Status404,
                },
            ],
        },
        {
            type: 'group',
            path: '/inventories',
            layout: CoreLayout.SidebarLayout,
            menuItems: [
                {
                    heading: 'Geral',
                    items: [
                        {
                            name: 'Minha Conta',
                            link: '/user/profile',
                            icon: PersonIcon,
                        },
                        {
                            name: 'Inventários',
                            link: '/inventories/list',
                            icon: RecentActors,
                        },
                    ]
                },
            ],
            children: [
                {
                    type: 'redirect',
                    path: '/inventories/',
                    redirectTo: '/inventories/list',
                    private: true,
                },
                {
                    type: 'page',
                    path: 'list',
                    component: DrHerancaComponent.InventoriesList,
                    private: true,
                },
            ],
        },
        {
            type: 'group',
            path: '/inventory',
            layout: CoreLayout.SidebarLayout,
            menuItems: [
                {
                    heading: 'Geral',
                    items: [
                        {
                            name: 'Minha Conta',
                            link: '/user/profile',
                            icon: PersonIcon,
                        },
                        {
                            name: 'Inventários',
                            link: '/inventories/list',
                            icon: RecentActors,
                        },
                    ]
                },
                {
                    heading: 'Inventário',
                    items: [
                        {
                            name: 'Árvore do Inventário',
                            link: '/inventory/home/:id?tab=0&subtab=0',
                            icon: TreeIcon,
                        },
                        {
                            name: 'Pessoas',
                            link: '/inventory/home/:id?tab=1&subtab=0',
                            icon: GroupIcon,
                            items: [
                                { name: 'De Cujus', link: '/inventory/home/:id?tab=1&subtab=0' },
                                { name: 'Inventariante', link: '/inventory/home/:id?tab=1&subtab=1' },
                                { name: 'Familiares', link: '/inventory/home/:id?tab=1&subtab=2' },
                            ],
                        },
                        {
                            name: 'Espólio',
                            link: '/inventory/home/:id?tab=2&subtab=0',
                            icon: HomeWorkIcon,
                            items: [
                                { name: 'Imóveis', link: '/inventory/home/:id?tab=2&subtab=0' },
                                { name: 'Veículos', link: '/inventory/home/:id?tab=2&subtab=1' },
                                { name: 'Saldos e Investimentos', link: '/inventory/home/:id?tab=2&subtab=2' },
                                { name: 'Participações Societárias', link: '/inventory/home/:id?tab=2&subtab=3' },
                                { name: 'Créditos', link: '/inventory/home/:id?tab=2&subtab=4' },
                                { name: 'Criptoativos', link: '/inventory/home/:id?tab=2&subtab=5' },
                                { name: 'Outros Bens', link: '/inventory/home/:id?tab=2&subtab=6' },
                                { name: 'Seguros', link: '/inventory/home/:id?tab=2&subtab=7' },
                                { name: 'Bens no Exterior', link: '/inventory/home/:id?tab=2&subtab=8' },
                            ],
                        },
                        {
                            name: 'Dívidas',
                            link: '/inventory/home/:id?tab=3&subtab=0',
                            icon: RequestQuoteIcon,
                            items: [
                                { name: 'Financiamentos de Bens', link: '/inventory/home/:id?tab=3&subtab=0' },
                                { name: 'Dívidas e Despesas', link: '/inventory/home/:id?tab=3&subtab=1' },
                            ],
                        },
                        {
                            name: 'Doações',
                            link: '/inventory/home/:id?tab=4&subtab=0',
                            icon: VolunteerActivismIcon,
                        },
                        {
                            name: 'Testamentos',
                            link: '/inventory/home/:id?tab=5&subtab=0',
                            icon: GavelIcon,
                            items: [
                                { name: 'Herdeiros Testamentários', link: '/inventory/home/:id?tab=5&subtab=0' },
                                { name: 'Legados', link: '/inventory/home/:id?tab=5&subtab=1' },
                            ],
                        },
                        {
                            name: 'Escritura',
                            link: '/inventory/home/:id?tab=6&subtab=0',
                            icon: DescriptionIcon,
                        },
                        {
                            name: 'Alertas',
                            link: '/inventory/home/:id?tab=7&subtab=0',
                            icon: InfoOutlinedIcon,
                        },
                        {
                            name: 'Relatório',
                            link: '/inventory/home/:id?tab=8&subtab=0',
                            icon: AssessmentIcon,
                            items: [
                                { name: 'Visão Geral', link: '/inventory/home/:id?tab=8&subtab=0' },
                                { name: 'Qualificação das Partes', link: '/inventory/home/:id?tab=8&subtab=1' },
                                { name: 'Bens do Espólio', link: '/inventory/home/:id?tab=8&subtab=2' },
                                { name: 'Dívidas e Doações', link: '/inventory/home/:id?tab=8&subtab=3' },
                                { name: 'Partilha', link: '/inventory/home/:id?tab=8&subtab=4' },
                                { name: 'Custos e Tributos', link: '/inventory/home/:id?tab=8&subtab=5' },
                                ...(!hideInventoryDocumentsSection
                                  ? [{ name: 'Exportação', link: '/inventory/home/:id?tab=8&subtab=6' }]
                                  : []),
                            ],
                        },
                    ]
                }
            ],
            children: [
                {
                    type: 'redirect',
                    path: '/inventory/',
                    redirectTo: '/inventory/home',
                    private: true,
                },
                {
                    type: 'page',
                    path: 'delete/:id',
                    component: DrHerancaComponent.InventoryDelete,
                    private: true,
                },
                {
                    type: 'page',
                    path: 'tree/:id',
                    component: DrHerancaComponent.InventoryFamilyTree,
                    private: true,
                },
                {
                    type: 'page',
                    path: 'home/:id',
                    component: DrHerancaComponent.InventoryHome,
                    private: true,
                },
                {
                    type: 'page',
                    path: 'report/:id',
                    component: DrHerancaComponent.InventoryHome,
                    private: true,
                },
                {
                    type: 'page',
                    path: 'home',
                    component: DrHerancaComponent.InventoryHome,
                    private: true,
                },
            ],
        },
        {
            type: 'group',
            path: 'user',
            layout: CoreLayout.SidebarLayout,
            menuItems: [
                {
                    heading: 'Geral',
                    items: [
                        {
                            name: 'Minha Conta',
                            link: '/user/profile',
                            icon: PersonIcon,
                        },
                        {
                            name: 'Inventários',
                            link: '/inventories/list',
                            icon: RecentActors,
                        },
                    ]
                },
            ],
            children: [
                {
                    type: 'page',
                    path: 'profile',
                    component: DrHerancaComponent.UserProfile,
                    private: true,
                },
            ],
        },
        {
            type: 'page',
            path: 'register',
            component: CoreComponent.UserSignUp,
            redirectIfAuthorized: true,
        },
        {
            type: 'page',
            path: 'login',
            component: CoreComponent.UserSignIn,
            redirectIfAuthorized: true,
        },
    ],
    
    signIn: {
        heading: 'Simplifique o cálculo de inventários',
        headingHighlight: 'cálculo de inventários',
        description: 'Uma ferramenta visual e intuitiva para advogados e profissionais da área, que simplifica a partilha de bens.',
        benefits: [
            'Árvore do inventário interativa',
            'Cálculos automatizados de meação e herança',
            'Relatórios detalhados para clientes',
            'Suporte técnico especializado'
        ],
    },
    
    signUp: {
        heading: 'Comece sua jornada com o Dr. Herança',
        headingHighlight: 'com o Dr. Herança',
        description: 'Crie sua conta gratuita e comece a calcular inventários com precisão e rapidez.',
        benefits: [
            'Árvore do inventário interativa',
            'Cálculos automatizados de meação e herança',
            'Relatórios detalhados para clientes',
            'Suporte técnico especializado'
        ],
    },
    
    status500: {
        title: 'Dr. Herança - Erro no Servidor',
        heading: 'Ocorreu um erro, por favor tente novamente mais tarde',
        description: 'O servidor encontrou um erro interno e não foi capaz de completar sua solicitação',
        refreshButtonText: 'Atualizar visualização',
        backButtonText: 'Voltar',
        rightPanelHeading: 'Dr. Herança - Cálculo de Inventário e Partilha de Bens',
        rightPanelDescription: 'Um sistema de cálculo de inventário e partilha de bens de alta performance construído com muitos componentes poderosos do Material-UI em várias niches de produtos para processos de desenvolvimento de aplicativos rápidos e perfeitos.',
        rightPanelButtonLink: '/inventories/list',
        rightPanelButtonText: 'Ir para o Painel Inicial',
    },
};